import {Values} from "../common-types/common-types";
import {enumValueOrDefault} from "../enum-utils/enum-utils";

export const VcfUrlFieldTypes = {
    Home: 'home',
    Work: 'work',
    Other: 'other'
} as const;

export type VcfUrlFieldTypes = Values<typeof VcfUrlFieldTypes>;

export const VcfPhoneFieldTypes = {
    Home: 'home',
    Work: 'work',
    Mobile: 'mobile',
    Main: 'main',
    Fax: 'fax',
    Pager: 'pager',
    Other: 'other',
    // iPhone: 'iphone', // iOS-specific label
} as const;

export type VcfPhoneFieldTypes = Values<typeof VcfPhoneFieldTypes>;

export const VcfEmailFieldTypes = {
    Home: 'home',
    Work: 'work',
    Other: 'other'
} as const;

export type VcfEmailFieldTypes = Values<typeof VcfEmailFieldTypes>;

export const VcfFieldsLabelingMethod = {
    AppleLabelingFormat: 'AppleLabelingFormat',
    UsingCustomTypes: 'UsingCustomTypes',
    FallbackToKnownTypes: 'FallbackToKnownTypes',
} as const;

export type VcfFieldsLabelingMethod = Values<typeof VcfFieldsLabelingMethod>;

export function vcfGenerate(
    options: {
        fieldsLabelingMethod?: VcfFieldsLabelingMethod;
        firstName: string;
        lastName: string;
        organization?: string;
        jobTitle?: string;
        emails?: { email: string; type?: VcfEmailFieldTypes }[]; // Allow multiple emails with optional types
        phoneNumbers?: { phoneNumber: string; type?: VcfPhoneFieldTypes }[]; // Allow multiple phone numbers with optional types
        urls?: { url: string; type?: string }[]; // Allow multiple URLs
        address?: string; // Assuming one address for simplicity, can be expanded
        note?: string;
        photoUrl?: string; // URL of the profile image
    }
): string {

    const {
        fieldsLabelingMethod = VcfFieldsLabelingMethod.UsingCustomTypes,
        lastName,
        firstName,
        organization,
        jobTitle,
        emails,
        phoneNumbers,
        urls,
        address,
        note,
        photoUrl
    } = options;

    let vCardString = `BEGIN:VCARD\nVERSION:3.0\n`;

    // Name
    vCardString += `N:${lastName};${firstName};;;\n`;
    vCardString += `FN:${firstName} ${lastName}\n`;

    // Organization
    if (organization) {
        vCardString += `ORG:${organization}\n`;
    }

    // Job Title
    if (jobTitle) {
        vCardString += `TITLE:${jobTitle}\n`;
    }

    // Emails
    emails?.forEach(({ email, type }) => {
        const typeStr = type ? `;TYPE=${type}` : '';
        vCardString += `EMAIL${typeStr}:${email}\n`;
    });

    // Phone Numbers
    phoneNumbers?.forEach(({ phoneNumber, type }) => {
        const typeStr = type ? `;TYPE=${type}` : '';
        vCardString += `TEL${typeStr}:${phoneNumber}\n`;
    });

    // URLs
    urls?.forEach(({url, type}, index) => {

        switch (fieldsLabelingMethod) {
            case VcfFieldsLabelingMethod.UsingCustomTypes: {
                const typeStr = type ? `;TYPE=${type}` : '';
                vCardString += `URL${typeStr}:${url}\n`;
                break;
            }
            case VcfFieldsLabelingMethod.FallbackToKnownTypes: {
                const typeStr = type ? `;TYPE=${enumValueOrDefault(VcfUrlFieldTypes, type, VcfUrlFieldTypes.Other)}` : '';
                vCardString += `URL${typeStr}:${url}\n`;
                break;
            }
            case VcfFieldsLabelingMethod.AppleLabelingFormat: {
                const itemNumber = index + 1;
                const label = type || 'Website';
                vCardString += `item${itemNumber}.URL:${url}\n`;
                vCardString += `item${itemNumber}.X-ABLabel:${label}\n`;
                break;
            }
        }
    });

    // Address
    if (address) {
        vCardString += `ADR:;;${address};;;;;\n`;
    }

    // Note
    if (note) {
        vCardString += `NOTE:${note.replace(/\n/g, '\\n')}\n`;
    }

    // Photo
    if (photoUrl) {
        // Check if it's a base64 encoded image
        if (photoUrl.startsWith('data:image')) {
            // Extract the content type and base64 data from the data URI
            const matches = photoUrl.match(/^data:(image\/\w+);base64,(.*)$/);
            if (matches) {
                const contentType = matches[1];
                const base64Data = matches[2];
                const type = contentType.split('/')[1].toUpperCase();
                vCardString += `PHOTO;ENCODING=b;TYPE=${type}:${base64Data}\n`;
            }
        } else {
            // If it's not base64, add the URL
            vCardString += `PHOTO;VALUE=URL:${photoUrl}\n`;
        }
    }

    vCardString += `END:VCARD`;

    return vCardString;
}
